import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowDownCircle, ArrowUpCircle, Bell, CalendarClock, Car,
  CreditCard, Key, Landmark, Percent, PiggyBank, Ship, TrendingUp, Wallet, Wrench,
} from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import StatusBadge from "../components/StatusBadge";
import { fmtDate, fmtMoney, todayISO } from "../utils/format";

const PALETTE = ["#0891B2", "#059669", "#D97706", "#7C3AED", "#0284C7", "#E11D48", "#0D9488"];
const moneyTick = (v) => (Math.abs(v) >= 1000000 ? `${(v / 1000000).toFixed(0)}M` : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v);

const ChartCard = ({ title, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
    <h3 className="font-heading mb-4 text-base font-semibold text-slate-800">{title}</h3>
    <div className="h-60">{children}</div>
  </div>
);

const TONES = {
  cyan: "bg-cyan-50 text-cyan-600", blue: "bg-blue-50 text-blue-600",
  emerald: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600",
  red: "bg-red-50 text-red-600", sky: "bg-sky-50 text-sky-600",
  purple: "bg-purple-50 text-purple-600", slate: "bg-slate-100 text-slate-600",
};

function Kpi({ icon: Icon, label, value, sub, tone = "cyan", to, testid }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => to && navigate(to)} data-testid={testid}
      className="group rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-cyan-300 hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</p>
          <p className="font-mono-num mt-1.5 truncate text-xl font-bold tracking-tight text-slate-900">{value}</p>
          {sub && <p className="mt-0.5 truncate text-xs text-slate-400">{sub}</p>}
        </div>
        {Icon && <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-110 ${TONES[tone]}`}><Icon className="h-4.5 w-4.5 h-5 w-5" /></div>}
      </div>
    </button>
  );
}

const PRESETS = [
  ["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"],
  ["month", "Ce mois"], ["year", "Cette année"], ["custom", "Personnalisé"],
];

function computeRange(p) {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (p === "today") return { from: iso(today), to: iso(today) };
  if (p === "7d") return { from: iso(new Date(Date.now() - 6 * 86400000)), to: iso(today) };
  if (p === "30d") return { from: iso(new Date(Date.now() - 29 * 86400000)), to: iso(today) };
  if (p === "month") return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) };
  if (p === "year") return { from: `${today.getFullYear()}-01-01`, to: iso(today) };
  return null;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const navigate = useNavigate();

  useEffect(() => {
    const range = preset === "custom" ? (custom.from && custom.to ? custom : null) : computeRange(preset);
    const params = new URLSearchParams();
    if (range) { params.set("date_from", range.from); params.set("date_to", range.to); }
    api.get(`/dashboard?${params}`).then((r) => setData(r.data)).catch(() => {});
  }, [preset, custom]);

  const charts = data?.charts;
  const kpis = data?.kpis;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 lg:text-3xl">Tableau de bord</h1>
          <p className="mt-1 text-sm text-slate-500">Cockpit de gestion — toutes les données sont calculées en temps réel depuis la base.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="period-filters">
          {PRESETS.map(([k, l]) => (
            <Button key={k} size="sm" variant={preset === k ? "default" : "outline"}
              className={preset === k ? "bg-cyan-600 hover:bg-cyan-700" : ""}
              onClick={() => setPreset(k)} data-testid={`preset-${k}`}>{l}</Button>
          ))}
          {preset === "custom" && (
            <>
              <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="w-36" data-testid="custom-from" />
              <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="w-36" data-testid="custom-to" />
            </>
          )}
        </div>
      </div>

      {!data ? (
        <div className="flex h-96 items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-cyan-600 border-t-transparent" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5" data-testid="kpi-stock-grid">
            <Kpi icon={Car} label="Parc total" value={kpis.total_vehicles} sub={`${kpis.stock} en stock`} to="/vehicules" testid="kpi-total-vehicles" />
            <Kpi icon={Car} label="Disponibles" value={kpis.disponibles} tone="emerald" to="/vehicules" testid="kpi-available" />
            <Kpi icon={Key} label="En location" value={kpis.en_location} tone="purple" to="/locations" testid="kpi-rented" />
            <Kpi icon={Car} label="Réservés" value={kpis.reserves} tone="blue" to="/reservations" testid="kpi-reserved" />
            <Kpi icon={TrendingUp} label="Vendus" value={kpis.vendus} tone="slate" to="/ventes" testid="kpi-sold" />
            <Kpi icon={Ship} label="En transit" value={kpis.en_transit} tone="sky" to="/importations" testid="kpi-transit" />
            <Kpi icon={Ship} label="Au port" value={kpis.au_port} tone="amber" to="/port-douane" testid="kpi-port" />
            <Kpi icon={Ship} label="En douane" value={kpis.en_dedouanement} tone="amber" to="/port-douane" testid="kpi-customs" />
            <Kpi icon={Wrench} label="En préparation" value={kpis.en_preparation} tone="slate" to="/vehicules" testid="kpi-preparation" />
            <Kpi icon={Wrench} label="Entretiens" value={data.maintenance_due.length} sub="à effectuer sous 30 j" tone="amber" to="/entretiens" testid="kpi-maintenance" />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="kpi-finance-grid">
            <Kpi icon={Wallet} label="Capital immobilisé" value={fmtMoney(kpis.capital_immobilise)} sub={`+30j : ${kpis.aging["30"].count} · +60j : ${kpis.aging["60"].count} · +90j : ${kpis.aging["90"].count}`} to="/rentabilite" testid="kpi-capital" />
            <Kpi icon={Car} label="Valeur totale du stock" value={fmtMoney(kpis.valeur_stock)} sub="Prix cibles" to="/vehicules" testid="kpi-stock-value" />
            <Kpi icon={TrendingUp} label="Chiffre d'affaires" value={fmtMoney(kpis.chiffre_affaires)} sub={`Ventes + locations (${data.period_label})`} tone="emerald" to="/ventes" testid="kpi-revenue" />
            <Kpi icon={PiggyBank} label="Bénéfice réalisé" value={fmtMoney(kpis.benefice)} tone="emerald" to="/rentabilite" testid="kpi-total-profit" />
            <Kpi icon={Percent} label="Marge moyenne" value={`${kpis.marge_moyenne} %`} sub={`ROI moyen ${kpis.roi_moyen} %`} tone="cyan" to="/rentabilite" testid="kpi-avg-margin" />
            <Kpi icon={PiggyBank} label="Bénéfice potentiel stock" value={fmtMoney(kpis.benefice_potentiel)} tone="emerald" to="/rentabilite" testid="kpi-potential-profit" />
            <Kpi icon={Landmark} label="Trésorerie" value={fmtMoney(kpis.tresorerie)} sub="Caisses + banques" tone="sky" to="/caisse" testid="kpi-treasury" />
            <Kpi icon={CreditCard} label="Créances clients" value={fmtMoney(kpis.creances_totales)} sub={`${kpis.paiements_retard.count} paiement(s) en retard (${fmtMoney(kpis.paiements_retard.amount)})`} tone="amber" to="/credits" testid="kpi-credits" />
            <Kpi icon={ArrowUpCircle} label={`Recettes (${data.period_label})`} value={fmtMoney(kpis.recettes_mois)} tone="emerald" to="/comptabilite" testid="kpi-period-income" />
            <Kpi icon={ArrowDownCircle} label={`Dépenses (${data.period_label})`} value={fmtMoney(kpis.depenses_mois)} tone="red" to="/depenses" testid="kpi-period-expenses" />
            <Kpi icon={CalendarClock} label="Échéances ≤ 7 jours" value={`${kpis.echeances_7j.count}`} sub={fmtMoney(kpis.echeances_7j.amount)} tone="amber" to="/credits" testid="kpi-due-7d" />
            <Kpi icon={CreditCard} label="Dettes fournisseurs" value={fmtMoney(kpis.dettes_fournisseurs)} tone="red" to="/fournisseurs" testid="kpi-supplier-debts" />
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <ChartCard title="Ventes par mois" testid="chart-sales">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.sales_by_month}>
                  <defs><linearGradient id="gS" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0891B2" stopOpacity={0.3} /><stop offset="100%" stopColor="#0891B2" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={moneyTick} tick={{ fontSize: 11 }} width={50} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Area type="monotone" dataKey="value" name="Ventes" stroke="#0891B2" fill="url(#gS)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Bénéfices par mois" testid="chart-profits">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.profit_by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={moneyTick} tick={{ fontSize: 11 }} width={50} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="value" name="Bénéfice" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Dépenses par mois" testid="chart-expenses-month">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.depenses_by_month}>
                  <defs><linearGradient id="gD" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E11D48" stopOpacity={0.25} /><stop offset="100%" stopColor="#E11D48" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={moneyTick} tick={{ fontSize: 11 }} width={50} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Area type="monotone" dataKey="value" name="Dépenses" stroke="#E11D48" fill="url(#gD)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Encaissements par mois" testid="chart-collections">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.encaissements_by_month}>
                  <defs><linearGradient id="gE" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#059669" stopOpacity={0.25} /><stop offset="100%" stopColor="#059669" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={moneyTick} tick={{ fontSize: 11 }} width={50} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Area type="monotone" dataKey="value" name="Encaissements" stroke="#059669" fill="url(#gE)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Dépenses par catégorie" testid="chart-expenses">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={charts.expenses_by_group} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                    {charts.expenses_by_group.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Évolution de la trésorerie" testid="chart-treasury">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={charts.treasury_evolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={moneyTick} tick={{ fontSize: 11 }} width={50} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Line type="monotone" dataKey="value" name="Trésorerie" stroke="#0284C7" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Créances clients par échéance" testid="chart-receivables">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.receivables_by_month}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={moneyTick} tick={{ fontSize: 11 }} width={50} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="value" name="Créances" fill="#D97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Évolution du stock (capital immobilisé)" testid="chart-stock">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={charts.stock_evolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={35} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" name="Véhicules" stroke="#7C3AED" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Ventes par marque" testid="chart-brand">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.sales_by_brand} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="value" name="Ventes" fill="#0891B2" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Ventes par modèle" testid="chart-model">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.sales_by_model} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tickFormatter={moneyTick} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="value" name="Ventes" fill="#0D9488" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Marges par véhicule vendu" testid="chart-margins">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.margins_by_vehicle}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tickFormatter={moneyTick} tick={{ fontSize: 11 }} width={50} />
                  <Tooltip formatter={(v, name, p) => [`${fmtMoney(v)} (${p.payload.margin}%)`, "Bénéfice"]} />
                  <Bar dataKey="value" name="Bénéfice" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Locations par mois" testid="chart-rentals">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.rentals_by_month}>
                  <defs><linearGradient id="gR" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7C3AED" stopOpacity={0.25} /><stop offset="100%" stopColor="#7C3AED" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tickFormatter={moneyTick} tick={{ fontSize: 11 }} width={50} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Area type="monotone" dataKey="value" name="Locations" stroke="#7C3AED" fill="url(#gR)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="panel-upcoming">
              <h3 className="font-heading mb-3 flex items-center gap-2 text-base font-semibold text-slate-800"><CalendarClock className="h-4 w-4 text-amber-500" /> Échéances proches</h3>
              <div className="space-y-2">
                {data.upcoming_installments.length === 0 && <p className="text-sm text-slate-400">Aucune échéance sous 30 jours.</p>}
                {data.upcoming_installments.map((i, k) => (
                  <button key={k} onClick={() => navigate("/credits")} className="w-full rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-left hover:bg-amber-100">
                    <p className="text-sm font-medium text-slate-800">{i.customer}</p>
                    <p className="text-xs text-slate-500">{fmtMoney(i.amount)} · {fmtDate(i.due_date)} · J-{i.days}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="panel-overdue">
              <h3 className="font-heading mb-3 flex items-center gap-2 text-base font-semibold text-slate-800"><AlertTriangle className="h-4 w-4 text-red-500" /> Paiements en retard</h3>
              <div className="space-y-2">
                {data.overdue_installments.length === 0 && <p className="text-sm text-slate-400">Aucun retard.</p>}
                {data.overdue_installments.map((i, k) => (
                  <button key={k} onClick={() => navigate("/credits")} className="w-full rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-left hover:bg-red-100">
                    <p className="text-sm font-medium text-slate-800">{i.customer}</p>
                    <p className="text-xs text-red-600">{fmtMoney(i.amount)} · retard de {-i.days} j</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="panel-top-profitable">
              <h3 className="font-heading mb-3 flex items-center gap-2 text-base font-semibold text-slate-800"><PiggyBank className="h-4 w-4 text-emerald-500" /> Les plus rentables</h3>
              <div className="space-y-2">
                {data.top_profitable.length === 0 && <p className="text-sm text-slate-400">Aucune vente clôturée.</p>}
                {data.top_profitable.map((v) => (
                  <button key={v.id} onClick={() => navigate(`/vehicules/${v.id}`)} className="w-full rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-left hover:bg-emerald-50">
                    <p className="text-sm font-medium text-slate-800">{v.brand} {v.model}</p>
                    <p className="font-mono-num text-xs text-emerald-600">+{fmtMoney(v.profit)} · {v.margin_pct}%</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="panel-alerts">
              <h3 className="font-heading mb-3 flex items-center gap-2 text-base font-semibold text-slate-800"><Bell className="h-4 w-4 text-cyan-500" /> Alertes prioritaires</h3>
              <div className="space-y-2">
                {data.alerts.length === 0 && <p className="text-sm text-slate-400">Aucune alerte non lue.</p>}
                {data.alerts.slice(0, 5).map((a) => (
                  <div key={a.id} className={`rounded-lg border px-3 py-2 ${a.severity === "danger" ? "border-red-100 bg-red-50" : a.severity === "warning" ? "border-amber-100 bg-amber-50" : "border-cyan-100 bg-cyan-50"}`}>
                    <p className="text-xs font-semibold text-slate-800">{a.title}</p>
                    <p className="text-xs text-slate-500">{a.message}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="panel-recent-vehicles">
              <h3 className="font-heading mb-3 text-base font-semibold text-slate-800">Véhicules récents</h3>
              <div className="space-y-2">
                {data.recent_vehicles.map((v) => (
                  <button key={v.id} onClick={() => navigate(`/vehicules/${v.id}`)} className="flex w-full items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-left hover:bg-slate-50">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{v.brand} {v.model} {v.year}</p>
                      <p className="font-mono-num text-xs text-slate-400">{v.internal_number} · coût {fmtMoney(v.cost_basis)}</p>
                    </div>
                    <StatusBadge status={v.status} />
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="panel-best-sales">
              <h3 className="font-heading mb-3 text-base font-semibold text-slate-800">Meilleures ventes</h3>
              <div className="space-y-2">
                {data.best_sales.length === 0 && <p className="text-sm text-slate-400">Aucune vente.</p>}
                {data.best_sales.map((s) => (
                  <button key={s.id} onClick={() => navigate("/ventes")} className="flex w-full items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-left hover:bg-slate-50">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{s.vehicle_label}</p>
                      <p className="text-xs text-slate-400">{s.sale_number} · {s.customer_name} · {fmtDate(s.sale_date)}</p>
                    </div>
                    <span className="font-mono-num text-sm font-bold text-slate-900">{fmtMoney(s.final_price)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
